import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useContext, useRef } from "react";
import { createPortal } from "react-dom";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import isToday from "date-fns/isToday";
import es from "date-fns/locale/es";
import "react-big-calendar/lib/css/react-big-calendar.css";
import TimeSlotPicker from "../components/TimeSlotPicker";
import PrestacionesModal from "../components/PrestacionesModal";
import { UserContext } from "../services/UserContext";
import { API, jsonAuthHeaders } from "../services/config";
import { localDateTimeToApi, parseApiDateTime } from "../services/datetime";
import "./CalendarioTurnero.css";

const STORAGE = {
  slot: "turnero-calendario-slot-minutes",
  vista: "turnero-calendario-vista",
  prof: "turnero-calendario-prof",
  horaDesde: "turnero-calendario-hora-desde",
  horaHasta: "turnero-calendario-hora-hasta",
};

const DEFAULT_HORA_DESDE = 6;
const DEFAULT_HORA_HASTA = 22;
const VALID_VIEWS = ["month", "week", "day", "agenda"];

const VIEW_OPTIONS = [
  { key: "month", label: "Mes" },
  { key: "week", label: "Semana" },
  { key: "day", label: "Día" },
  { key: "agenda", label: "Agenda" },
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => ({
  value: h,
  label: `${String(h).padStart(2, "0")}:00`,
}));

function readStoredSlotMinutes() {
  try {
    const raw = localStorage.getItem(STORAGE.slot);
    const n = raw ? Number(raw) : 30;
    if (n === 15 || n === 30 || n === 60) return n;
  } catch (_) {}
  return 30;
}

function readStoredView() {
  try {
    const raw = localStorage.getItem(STORAGE.vista);
    if (raw && VALID_VIEWS.includes(raw)) return raw;
  } catch (_) {}
  return "week";
}

function readStoredProf() {
  try {
    const raw = localStorage.getItem(STORAGE.prof);
    if (raw) return raw;
  } catch (_) {}
  return "Todos";
}

function readStoredHour(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    const n = raw !== null ? Number(raw) : fallback;
    if (Number.isInteger(n) && n >= 0 && n <= 23) return n;
  } catch (_) {}
  return fallback;
}

function formatHourLabel(hour) {
  return `${String(hour).padStart(2, "0")}:00`;
}

function formatDayAbbr(date) {
  return format(date, "EEE", { locale: es }).replace(/\./g, "").toLowerCase();
}

function formatMonthAbbr(date) {
  return format(date, "MMM", { locale: es }).replace(/\./g, "").toLowerCase();
}

function formatDayLine(date) {
  return `${capitalizeFirst(formatDayAbbr(date))} ${format(date, "d")} ${formatMonthAbbr(date)}`;
}

function CalendarioColDayHeader({ date }) {
  const today = isToday(date);
  return (
    <div className={`cal-col-day-header${today ? " is-today" : ""}`} role="columnheader">
      <span className="cal-col-day-header__abbr">{formatDayAbbr(date)}</span>
      <span className="cal-col-day-header__num">{format(date, "d")}</span>
    </div>
  );
}

function CalendarioEmptyDayHeader() {
  return <span className="cal-day-column-header-hidden" aria-hidden="true" />;
}

function CalendarioDayAllDayLabel({ label, isToday, rootRef, fechaKey }) {
  const [target, setTarget] = useState(null);

  useLayoutEffect(() => {
    const findTarget = () => rootRef.current?.querySelector(".calendario-view--day .rbc-allday-cell");
    setTarget(findTarget());
    const timer = setTimeout(() => setTarget(findTarget()), 0);
    return () => clearTimeout(timer);
  }, [rootRef, fechaKey, label]);

  if (!target) return null;

  return createPortal(
    <div
      className={`cal-col-day-header cal-col-day-header--line${isToday ? " is-today" : ""}`}
      role="columnheader"
      aria-label={label}
    >
      <span className="cal-col-day-header__line">{label}</span>
    </div>,
    target
  );
}

function CalendarioTimeGutterHeader({ slotMinutes, onSlotChange }) {
  return (
    <div className="time-gutter-interval">
      <label htmlFor="cal-slot-interval" className="time-gutter-interval__label">Intervalo</label>
      <select
        id="cal-slot-interval"
        className="time-gutter-interval__select"
        value={slotMinutes}
        onChange={e => onSlotChange(Number(e.target.value))}
        aria-label="Intervalo de grilla horaria"
      >
        <option value={15}>15 min</option>
        <option value={30}>30 min</option>
        <option value={60}>1 h</option>
      </select>
    </div>
  );
}

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(min-width: 900px)").matches
  );

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 900px)");
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return isDesktop;
}

function CalendarioProfesionalFiltro({ profSel, onProfChange, profesionales }) {
  return (
    <div className="cal-prof-filtro">
      <div className="profesionales-chips" aria-hidden="true">
        {profesionales.map(p => (
          <span
            key={p.id}
            className="chip-profesional"
            style={{ background: p.color || "#032548" }}
          >
            {p.apellido}
          </span>
        ))}
      </div>
      <select
        name="cal_filtro_profesional"
        className="select-profesional cal-prof-filtro__select"
        value={profSel}
        onChange={e => onProfChange(e.target.value)}
        aria-label="Filtrar por profesional"
      >
        <option value="Todos">Todos los profesionales</option>
        {profesionales.map(p => (
          <option key={p.id} value={p.id}>{p.apellido}, {p.nombre}</option>
        ))}
      </select>
    </div>
  );
}

function CalendarioEstadosPanel() {
  return (
    <div className="cal-config-estados" aria-label="Leyenda de estados de turnos">
      <span className="cal-config-estado cal-config-estado--confirmado">✅ Confirmado</span>
      <span className="cal-config-estado cal-config-estado--pendiente">⏳ Pendiente</span>
      <span className="cal-config-estado cal-config-estado--cancelado">❌ Cancelado</span>
    </div>
  );
}

function CalendarioEstadosLeyenda() {
  return (
    <div className="calendario-leyenda calendario-leyenda--compact" aria-label="Leyenda de estados de turnos">
      <span className="calendario-leyenda__item">
        <span className="calendario-leyenda__chip calendario-leyenda__chip--confirmado">✅ Turno</span>
        <span className="calendario-leyenda__label">Confirmado</span>
      </span>
      <span className="calendario-leyenda__item">
        <span className="calendario-leyenda__chip calendario-leyenda__chip--pendiente">⏳ Turno</span>
        <span className="calendario-leyenda__label">Pendiente</span>
      </span>
      <span className="calendario-leyenda__item">
        <span className="calendario-leyenda__chip calendario-leyenda__chip--cancelado">❌ Turno</span>
        <span className="calendario-leyenda__label">Cancelado</span>
      </span>
    </div>
  );
}

function CalendarioDesktopBar({ profSel, onProfChange, profesionales }) {
  return (
    <div className="cal-desktop-bar">
      <CalendarioProfesionalFiltro
        profSel={profSel}
        onProfChange={onProfChange}
        profesionales={profesionales}
      />
      <CalendarioEstadosLeyenda />
    </div>
  );
}

function CalendarioToolbarCompacto({ label, vista, onNavigate, onToggleConfig, hasActiveFilters }) {
  const displayLabel = vista === "day" ? "" : label;
  return (
    <div className="cal-toolbar">
      <div className="cal-toolbar__nav">
        <button
          type="button"
          className="cal-toolbar__btn"
          onClick={() => onNavigate("PREV")}
          aria-label="Período anterior"
        >
          ‹
        </button>
        <button
          type="button"
          className="cal-toolbar__btn cal-toolbar__btn--today"
          onClick={() => onNavigate("TODAY")}
        >
          Hoy
        </button>
        <button
          type="button"
          className="cal-toolbar__btn"
          onClick={() => onNavigate("NEXT")}
          aria-label="Período siguiente"
        >
          ›
        </button>
      </div>
      {displayLabel ? (
        <span className="cal-toolbar__label" aria-live="polite">{displayLabel}</span>
      ) : (
        <span className="cal-toolbar__label cal-toolbar__label--empty" aria-hidden="true" />
      )}
      <button
        type="button"
        className={`cal-toolbar__config-btn${hasActiveFilters ? " has-filters" : ""}`}
        onClick={onToggleConfig}
        aria-label="Configuración del calendario"
      >
        <svg className="cal-toolbar__config-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M4 6h16M4 12h16M4 18h16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <circle cx="8" cy="6" r="2" fill="currentColor" />
          <circle cx="16" cy="12" r="2" fill="currentColor" />
          <circle cx="10" cy="18" r="2" fill="currentColor" />
        </svg>
        {hasActiveFilters && <span className="cal-toolbar__badge" aria-hidden="true" />}
      </button>
    </div>
  );
}

function CalendarioConfigPanel({
  vista,
  onViewChange,
  profSel,
  onProfChange,
  profesionales,
  horaDesde,
  horaHasta,
  onHoraDesdeChange,
  onHoraHastaChange,
  onClose,
}) {
  return (
    <div className="cal-config-panel" role="dialog" aria-label="Configuración del calendario">
      <div className="cal-config-panel__header">
        <h3 className="cal-config-panel__title">Configuración</h3>
        <button type="button" className="cal-config-panel__close" onClick={onClose} aria-label="Cerrar">
          ×
        </button>
      </div>

      <section className="cal-config-section">
        <h4 className="cal-config-section__title">Vista</h4>
        <div className="cal-config-view-btns">
          {VIEW_OPTIONS.map(v => (
            <button
              key={v.key}
              type="button"
              className={`cal-config-view-btn${vista === v.key ? " is-active" : ""}`}
              onClick={() => onViewChange(v.key)}
            >
              {v.label}
            </button>
          ))}
        </div>
      </section>

      <section className="cal-config-section cal-config-section--mobile-only">
        <h4 className="cal-config-section__title">Profesional</h4>
        <CalendarioProfesionalFiltro
          profSel={profSel}
          onProfChange={onProfChange}
          profesionales={profesionales}
        />
      </section>

      <section
        className="cal-config-section cal-config-section--estados cal-config-section--mobile-only"
        aria-label="Leyenda de estados de turnos"
      >
        <h4 className="cal-config-section__title">Estados</h4>
        <CalendarioEstadosPanel />
      </section>

      <section className="cal-config-section">
        <h4 className="cal-config-section__title">Horarios</h4>
        <p className="cal-config-section__hint">
          Definí qué horas mostrar en la grilla (ej. ocultar de 00:00 a 06:00). Solo aplica en Semana y Día.
        </p>
        <div className="cal-config-hours">
          <label className="cal-config-hours__field">
            <span className="cal-config-hours__label">Mostrar desde</span>
            <select
              className="cal-config-select"
              value={horaDesde}
              onChange={e => onHoraDesdeChange(Number(e.target.value))}
              aria-label="Primera hora visible"
            >
              {HOUR_OPTIONS.map(h => (
                <option key={`visible-desde-${h.value}`} value={h.value}>{h.label}</option>
              ))}
            </select>
          </label>
          <label className="cal-config-hours__field">
            <span className="cal-config-hours__label">Mostrar hasta</span>
            <select
              className="cal-config-select"
              value={horaHasta}
              onChange={e => onHoraHastaChange(Number(e.target.value))}
              aria-label="Última hora visible"
            >
              {HOUR_OPTIONS.map(h => (
                <option key={`visible-hasta-${h.value}`} value={h.value}>{h.label}</option>
              ))}
            </select>
          </label>
        </div>
        <p className="cal-config-hours__preview">
          Grilla visible: {formatHourLabel(horaDesde)} – {formatHourLabel(horaHasta)}
        </p>
      </section>

      <div className="cal-config-panel__footer">
        <button type="button" className="cal-config-panel__done" onClick={onClose}>
          Listo
        </button>
      </div>
    </div>
  );
}

function getStepAndTimeslots(slotMinutes) {
  switch (slotMinutes) {
    case 15: return { step: 15, timeslots: 4 };
    case 60: return { step: 60, timeslots: 1 };
    case 30: default: return { step: 30, timeslots: 2 };
  }
}

const locales = { es };

function capitalizeFirst(text) {
  if (!text) return text;
  return text.charAt(0).toLocaleUpperCase("es") + text.slice(1);
}

function formatEs(date, formatStr, options) {
  return capitalizeFirst(format(date, formatStr, options));
}

const localizer = dateFnsLocalizer({
  format: formatEs,
  parse,
  startOfWeek: (date) => startOfWeek(date, { locale: es }),
  getDay,
  locales,
});

/** Número internacional para enlace wa.me (Argentina por defecto) */
function normalizePhoneForWaMe(telefono) {
  const clean = String(telefono || "").replace(/\D/g, "");
  if (!clean) return "";
  if (clean.startsWith("54")) return clean;
  const local = clean.startsWith("0") ? clean.slice(1) : clean;
  if (local.startsWith("9") && local.length >= 10) return `54${local}`;
  return `54${local}`;
}

/** Texto y tipo visual para el estado de WhatsApp post-guardado */
function getWaStatusDisplay(waStatus) {
  if (!waStatus) return null;
  if (waStatus.sent) {
    return { tone: "wa-sent", text: "📲 WhatsApp enviado al paciente" };
  }
  if (waStatus.queued) {
    return {
      tone: "wa-pending",
      text: waStatus.message || "📲 WhatsApp en envío (puede tardar unos segundos)",
    };
  }
  return {
    tone: "wa-not-sent",
    text: `⚠️ WhatsApp no enviado: ${waStatus.reason || waStatus.message || "Error desconocido"}`,
  };
}

const EMPTY_NUEVO_PACIENTE = { nombre: "", apellido: "", mail: "", telefono: "", dni: "" };
const EMPTY_NUEVA_PRESTACION = { nombre: "", descripcion: "", precio: "", duracion_minutos: "" };

/** Normaliza fechas del API (YYYY-MM-DD o ISO) a YYYY-MM-DD */
function toDateOnly(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

/** Parsea YYYY-MM-DD (o ISO) a Date local a medianoche */
function parseLocalDate(value) {
  const [y, m, d] = toDateOnly(value).split("-").map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0);
}

/** Fin exclusivo allDay: medianoche del día posterior al último día bloqueado */
function endExclusiveLocal(finValue) {
  const d = parseLocalDate(finValue);
  d.setDate(d.getDate() + 1);
  return d;
}

/** Lista de YYYY-MM-DD entre dos fechas inclusive */
function eachDayInRange(startStr, endStr) {
  const days = [];
  const cur = new Date(`${toDateOnly(startStr)}T12:00:00`);
  const end = new Date(`${toDateOnly(endStr)}T12:00:00`);
  while (cur <= end) {
    days.push(format(cur, "yyyy-MM-dd"));
    cur.setDate(cur.getDate() + 1);
  }
  return days;
}

/** Formatea un Date como "YYYY-MM-DDTHH:MM" en hora LOCAL (sin conversión UTC). */
function toLocalDT(date) {
  const d = date instanceof Date ? date : new Date(date);
  const pad = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Suma minutos a un string "YYYY-MM-DDTHH:MM" (hora local) y devuelve el mismo formato. */
function addMinutesLocal(datetimeLocalStr, minutes) {
  const d = new Date(datetimeLocalStr); // se interpreta como hora local
  d.setMinutes(d.getMinutes() + minutes);
  return toLocalDT(d);
}

export default function CalendarioTurnero() {
  const { user } = useContext(UserContext);

  // ── Calendario ──
  const [vista, setVista] = useState(readStoredView);
  const [fechaCalendario, setFechaCalendario] = useState(() => new Date());
  const [eventos, setEventos] = useState([]);
  const [profesionales, setProfesionales] = useState([]);
  const [pacientes, setPacientes] = useState([]);
  const [profSel, setProfSel] = useState(readStoredProf);
  const [loading, setLoading] = useState(false);
  const [slotMinutes, setSlotMinutes] = useState(readStoredSlotMinutes);
  const [horaDesde, setHoraDesde] = useState(() => readStoredHour(STORAGE.horaDesde, DEFAULT_HORA_DESDE));
  const [horaHasta, setHoraHasta] = useState(() => readStoredHour(STORAGE.horaHasta, DEFAULT_HORA_HASTA));
  const [configOpen, setConfigOpen] = useState(false);
  const [bloques, setBloques] = useState([]);
  const calendarioContainerRef = useRef(null);
  const isDesktop = useIsDesktop();

  // ── Modal ──
  const [modal, setModal] = useState({ open: false, data: null, slot: null });
  const [saveResult, setSaveResult] = useState(null); // null | { saved, waStatus?, errorMsg? }
  const [waInlineStatus, setWaInlineStatus] = useState(null); // para reenvío manual
  const [cancelActionStatus, setCancelActionStatus] = useState(null); // feedback cancelar/reactivar
  const [formErrors, setFormErrors] = useState([]); // validación inline del formulario

  // ── Campos del formulario de turno ──
  const [modalProfesionalId, setModalProfesionalId] = useState("");
  const [practicasProf, setPracticasProf] = useState([]);
  const [servicioSel, setServicioSel] = useState("");
  const [montoTurno, setMontoTurno] = useState("");
  const [tituloTurno, setTituloTurno] = useState("");
  const [timeSlotStart, setTimeSlotStart] = useState("");
  const [timeSlotEnd, setTimeSlotEnd] = useState("");

  // ── Nuevo paciente (atajo inline) ──
  const [isNuevoPaciente, setIsNuevoPaciente] = useState(false);
  const [nuevoPaciente, setNuevoPaciente] = useState(EMPTY_NUEVO_PACIENTE);

  // ── Nueva prestación (atajo inline) ──
  const [isNuevaPrestacion, setIsNuevaPrestacion] = useState(false);
  const [nuevaPrestacion, setNuevaPrestacion] = useState(EMPTY_NUEVA_PRESTACION);

  // ── Modal de prestaciones cobradas (conservado para uso futuro) ──
  const [isPrestacionesModalOpen, setIsPrestacionesModalOpen] = useState(false);
  const [selectedPacienteForPrestacion, setSelectedPacienteForPrestacion] = useState(null);

  // ── Duración del servicio seleccionado (para sincronizar hora fin) ──
  const servicioSelObj = practicasProf.find(s => String(s.id) === String(servicioSel));
  const currentServiceDuration = isNuevaPrestacion && nuevaPrestacion.duracion_minutos
    ? parseInt(nuevaPrestacion.duracion_minutos, 10)
    : servicioSelObj?.duracion_minutos ?? null;

  // ── Polling: actualiza confirmación cuando el modal está abierto ──
  useEffect(() => {
    if (!modal.open || !modal.data) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API}/turnos/${modal.data.id}`, { headers: jsonAuthHeaders() });
        const data = await res.json();
        if (
          data.success &&
          (data.data.confirmado !== modal.data.confirmado ||
            data.data.cancelado !== modal.data.cancelado)
        ) {
          setModal(prev => ({
            ...prev,
            data: {
              ...data.data,
              start: parseApiDateTime(data.data.start),
              end: parseApiDateTime(data.data.end),
            },
          }));
          fetchTurnos();
        }
      } catch (e) {
        console.error("Error en polling:", e);
      }
    }, 5000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal.open, modal.data]);

  const { step, timeslots } = getStepAndTimeslots(slotMinutes);

  const hasActiveFilters = (!isDesktop && profSel !== "Todos")
    || horaDesde !== DEFAULT_HORA_DESDE
    || horaHasta !== DEFAULT_HORA_HASTA;

  const { minTime, maxTime } = useMemo(() => {
    const desde = Math.min(horaDesde, horaHasta - 1);
    const hasta = Math.max(horaHasta, desde + 1);
    const min = new Date();
    min.setHours(desde, 0, 0, 0);
    const max = new Date();
    max.setHours(Math.min(23, hasta), 59, 59, 999);
    return { minTime: min, maxTime: max };
  }, [horaDesde, horaHasta]);

  const handleNavigate = useCallback((newDate) => {
    setFechaCalendario(newDate);
  }, []);

  const handleDrillDown = useCallback((date, _view, drilldownView) => {
    if (vista === "week" || vista === "day") return;
    if (drilldownView) {
      setVista(drilldownView);
      setFechaCalendario(date);
    }
  }, [vista]);

  const handleHoraDesdeChange = useCallback((value) => {
    setHoraDesde(value);
    setHoraHasta(prev => (value >= prev ? Math.min(23, value + 1) : prev));
  }, []);

  const handleHoraHastaChange = useCallback((value) => {
    setHoraHasta(value);
    setHoraDesde(prev => (value <= prev ? Math.max(0, value - 1) : prev));
  }, []);

  const calendarComponents = useMemo(() => ({
    toolbar: (props) => (
      <CalendarioToolbarCompacto
        {...props}
        vista={vista}
        onToggleConfig={() => setConfigOpen(open => !open)}
        hasActiveFilters={hasActiveFilters}
      />
    ),
    timeGutterHeader: () => (
      <CalendarioTimeGutterHeader
        slotMinutes={slotMinutes}
        onSlotChange={setSlotMinutes}
      />
    ),
    week: {
      header: CalendarioColDayHeader,
    },
    day: {
      header: CalendarioEmptyDayHeader,
    },
    event: ({ event }) => {
      if (event.type === "block") {
        return (
          <span className="evento-bloqueo-label">
            🚫 {event.profesional && profSel === "Todos" ? `${event.profesional} · ` : ""}{event.title}
          </span>
        );
      }
      return (
        <span
          className={[
            event.cancelado ? "evento-cancelado-label" : "",
            !event.cancelado && event.confirmado ? "evento-confirmado-label" : "",
          ].join(" ").trim()}
        >
          {event.cancelado ? "❌ Cancelado · " : ""}
          {!event.cancelado && event.confirmado ? "✅ " : ""}
          {!event.cancelado && !event.confirmado ? "⏳ " : ""}
          {event.title}
        </span>
      );
    },
  }), [vista, slotMinutes, profSel, hasActiveFilters]);

  useEffect(() => {
    try { localStorage.setItem(STORAGE.slot, String(slotMinutes)); } catch (_) {}
  }, [slotMinutes]);

  useEffect(() => {
    try { localStorage.setItem(STORAGE.vista, vista); } catch (_) {}
  }, [vista]);

  useEffect(() => {
    try { localStorage.setItem(STORAGE.prof, profSel); } catch (_) {}
  }, [profSel]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE.horaDesde, String(horaDesde));
      localStorage.setItem(STORAGE.horaHasta, String(horaHasta));
    } catch (_) {}
  }, [horaDesde, horaHasta]);

  // ── Carga de datos ──

  const fetchBloques = useCallback(async (profs) => {
    if (!profs || profs.length === 0) return;
    try {
      const all = await Promise.all(
        profs.map(p =>
          fetch(`${API}/profesionales/${p.id}/bloques`, { headers: jsonAuthHeaders() })
            .then(r => r.json())
            .then(d => d.success ? d.data : [])
            .catch(() => [])
        )
      );
      setBloques(all.flat());
    } catch (e) { console.error("Error cargando bloques:", e); }
  }, []);

  const fetchProfesionales = useCallback(async () => {
    try {
      const res = await fetch(`${API}/profesionales`, { headers: jsonAuthHeaders() });
      const data = await res.json();
      if (data.success) {
        const profs = data.data.filter(p => p.activo);
        setProfesionales(profs);
        fetchBloques(profs);
      }
    } catch (e) { console.error("Error cargando profesionales:", e); }
  }, [fetchBloques]);

  const fetchPacientes = useCallback(async () => {
    try {
      const res = await fetch(`${API}/pacientes`, { headers: jsonAuthHeaders() });
      const data = await res.json();
      if (data.success) setPacientes(data.data);
    } catch (e) { console.error("Error cargando pacientes:", e); }
  }, []);

  const fetchTurnos = useCallback(async () => {
    setLoading(true);
    try {
      const profQs = user?.role === "profesional" && user?.profesional?.id
        ? `?profesional_id=${user.profesional.id}` : "";
      const res = await fetch(`${API}/turnos${profQs}`, { headers: jsonAuthHeaders() });
      const data = await res.json();
      if (data.success) {
        setEventos(data.data.map(t => ({
          ...t,
          start: parseApiDateTime(t.start),
          end: parseApiDateTime(t.end),
        })));
      }
    } catch (e) { console.error("Error cargando turnos:", e); }
    finally { setLoading(false); }
  }, [user]);

  const fetchPracticasProfesional = useCallback(async (profId) => {
    if (!profId) { setPracticasProf([]); return; }
    try {
      const res = await fetch(`${API}/profesionales/${profId}/practicas`, { headers: jsonAuthHeaders() });
      const data = await res.json();
      setPracticasProf(data.success ? data.data : []);
    } catch (e) { setPracticasProf([]); }
  }, []);

  useEffect(() => {
    fetchProfesionales();
    fetchPacientes();
    fetchTurnos();
  }, [fetchProfesionales, fetchPacientes, fetchTurnos]);

  // ── Filtro y bloques ──

  const eventosFiltrados = profSel === "Todos"
    ? eventos
    : eventos.filter(ev => String(ev.profesional_id) === String(profSel));

  const bloquesRelevantes = useMemo(() => {
    if (profSel === "Todos") return bloques;
    return bloques.filter(b => String(b.profesional_id) === String(profSel));
  }, [bloques, profSel]);

  const bloquesEventos = useMemo(() => {
    return bloquesRelevantes.map(b => {
      const inicio = toDateOnly(b.fecha_inicio);
      const fin = toDateOnly(b.fecha_fin);
      const prof = profesionales.find(p => String(p.id) === String(b.profesional_id));
      return {
        id: `block-${b.id}`,
        type: "block",
        title: b.razon || "Sin disponibilidad",
        start: parseLocalDate(inicio),
        end: endExclusiveLocal(fin),
        allDay: true,
        profesional_id: b.profesional_id,
        profesional: prof ? `${prof.apellido}, ${prof.nombre}` : "",
        color: prof?.color || "#94a3b8",
        bloque: b,
      };
    });
  }, [bloquesRelevantes, profesionales]);

  const diasBloqueados = useMemo(() => {
    const set = new Set();
    bloquesRelevantes.forEach(b => {
      eachDayInRange(b.fecha_inicio, b.fecha_fin).forEach(d => set.add(d));
    });
    return set;
  }, [bloquesRelevantes]);

  const dayPropGetter = useCallback((date) => {
    const ds = format(date, "yyyy-MM-dd");
    if (diasBloqueados.has(ds)) {
      return {
        className: "dia-bloqueado",
        style: { backgroundColor: "rgba(254, 226, 226, 0.45)" },
      };
    }
    return {};
  }, [diasBloqueados]);

  const slotPropGetter = useCallback((date) => {
    const ds = format(date, "yyyy-MM-dd");
    if (diasBloqueados.has(ds)) {
      return {
        className: "dia-bloqueado-slot",
        style: { backgroundColor: "rgba(254, 226, 226, 0.25)" },
      };
    }
    return {};
  }, [diasBloqueados]);

  // ── Helpers de modal ──

  const resetModalState = () => {
    setSaveResult(null);
    setWaInlineStatus(null);
    setCancelActionStatus(null);
    setFormErrors([]);
    setServicioSel("");
    setMontoTurno("");
    setTituloTurno("");
    setIsNuevoPaciente(false);
    setNuevoPaciente(EMPTY_NUEVO_PACIENTE);
    setIsNuevaPrestacion(false);
    setNuevaPrestacion(EMPTY_NUEVA_PRESTACION);
    setPracticasProf([]);
  };

  const closeModal = useCallback(() => {
    setModal({ open: false, data: null, slot: null });
    setTimeSlotStart("");
    setTimeSlotEnd("");
    resetModalState();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Abrir modal ──

  const handleSelectSlot = slotInfo => {
    resetModalState();
    const profId = user?.role === "profesional" ? String(user?.profesional?.id || "") : "";
    setModalProfesionalId(profId);
    setTimeSlotStart(toLocalDT(slotInfo.start));
    setTimeSlotEnd(toLocalDT(slotInfo.end));
    setModal({ open: true, data: null, slot: slotInfo });
    if (profId) fetchPracticasProfesional(profId);
  };

  const handleSelectEvent = event => {
    if (event.type === "block") return;
    resetModalState();
    setMontoTurno(event.monto || "");
    setTituloTurno(event.title || "");
    setModalProfesionalId(String(event.profesional_id || ""));
    setTimeSlotStart(toLocalDT(event.start));
    setTimeSlotEnd(toLocalDT(event.end));
    setModal({ open: true, data: event, slot: null });
    if (event.profesional_id) fetchPracticasProfesional(event.profesional_id);
  };

  // ── Handlers del formulario ──

  const handleProfesionalChange = e => {
    const profId = e.target.value;
    setModalProfesionalId(profId);
    fetchPracticasProfesional(profId);
    setServicioSel("");
    setMontoTurno("");
    setTituloTurno("");
  };

  const handleServicioChange = e => {
    const practicaId = e.target.value;
    setServicioSel(practicaId);
    if (!practicaId) { setTituloTurno(""); setMontoTurno(""); return; }
    const svc = practicasProf.find(s => String(s.id) === String(practicaId));
    if (svc) {
      setTituloTurno(svc.nombre);
      setMontoTurno(svc.costo_base != null ? String(svc.costo_base) : "");
      if (svc.duracion_minutos && timeSlotStart) {
        setTimeSlotEnd(addMinutesLocal(timeSlotStart, svc.duracion_minutos));
      }
    }
  };

  // ── Guardar ──

  const handleSave = async e => {
    e.preventDefault();
    const form = e.target;
    const errors = [];

    if (!isNuevaPrestacion && !servicioSel && !modal.data) {
      errors.push("Seleccioná un servicio o creá una nueva prestación.");
    }
    if (isNuevaPrestacion && !nuevaPrestacion.nombre.trim()) {
      errors.push("Completá el nombre de la nueva prestación.");
    }
    if (
      isNuevoPaciente &&
      (!nuevoPaciente.nombre.trim() || !nuevoPaciente.apellido.trim() || !nuevoPaciente.dni.trim())
    ) {
      errors.push("Completá nombre, apellido y DNI del nuevo paciente.");
    }
    const pacienteId = !isNuevoPaciente && form.paciente_id?.value ? Number(form.paciente_id.value) : null;
    if (!isNuevoPaciente && !pacienteId) {
      errors.push("Seleccioná un paciente o usá el atajo «Nuevo paciente».");
    }

    if (errors.length) {
      setFormErrors(errors);
      return;
    }
    setFormErrors([]);

    const profesionalId = user?.role === "profesional"
      ? (user?.profesional?.id || null)
      : (form.profesional_id?.value ? Number(form.profesional_id.value) : null);
    const profObj = profesionales.find(p => p.id === profesionalId);
    const pacienteObj = pacientes.find(p => p.id === pacienteId);

    // Verificar bloqueo del profesional en la fecha del turno
    if (profesionalId && timeSlotStart && !modal.data) {
      const fechaTurno = timeSlotStart.split("T")[0];
      const bloqueActivo = bloques.find(b =>
        String(b.profesional_id) === String(profesionalId) &&
        toDateOnly(b.fecha_inicio) <= fechaTurno &&
        toDateOnly(b.fecha_fin) >= fechaTurno
      );
      if (bloqueActivo) {
        const motivo = bloqueActivo.razon ? ` (${bloqueActivo.razon})` : "";
        const profNombre = profObj ? `${profObj.apellido}, ${profObj.nombre}` : "El profesional";
        const ok = window.confirm(
          `${profNombre} tiene registrada una licencia o no disponibilidad en esta fecha${motivo}.\n\n¿Desea crear el turno de todas formas?`
        );
        if (!ok) return;
      }
    }

    // Crear nueva prestación si aplica
    if (isNuevaPrestacion && nuevaPrestacion.nombre.trim()) {
      if (!profesionalId) {
        setFormErrors(["Seleccioná un profesional antes de crear la prestación."]);
        return;
      }
      const practicaRes = await fetch(`${API}/profesionales/${profesionalId}/practicas`, {
        method: "POST",
        headers: jsonAuthHeaders(),
        body: JSON.stringify({
          nombre: nuevaPrestacion.nombre,
          descripcion: nuevaPrestacion.descripcion || null,
          costo_base: nuevaPrestacion.precio || null,
          duracion_minutos: nuevaPrestacion.duracion_minutos || null,
        }),
      });
      const practicaData = await practicaRes.json();
      if (!practicaRes.ok || !practicaData.success) {
        setFormErrors([practicaData.message || "No se pudo crear la nueva prestación"]);
        return;
      }
      setPracticasProf(prev => [...prev, practicaData.data]);
    }

    const titleFinal = tituloTurno || nuevaPrestacion.nombre;
    const payload = {
      title:          titleFinal,
      profesional_id: profesionalId,
      profesional:    profObj ? `${profObj.apellido}, ${profObj.nombre}` : titleFinal,
      color:          profObj?.color || "#032548",
      paciente_id:    pacienteId,
      paciente:       pacienteObj
        ? `${pacienteObj.apellido} ${pacienteObj.nombre}`
        : isNuevoPaciente ? `${nuevoPaciente.apellido} ${nuevoPaciente.nombre}`.trim() : null,
      paciente_nuevo: isNuevoPaciente ? {
        nombre:   nuevoPaciente.nombre,
        apellido: nuevoPaciente.apellido,
        telefono: nuevoPaciente.telefono,
        mail:     nuevoPaciente.mail,
        dni:      nuevoPaciente.dni,
      } : null,
      mail:          isNuevoPaciente ? nuevoPaciente.mail : (pacienteObj?.mail || null),
      telefono:      isNuevoPaciente ? nuevoPaciente.telefono : (pacienteObj?.celular || null),
      start:         localDateTimeToApi(timeSlotStart),
      end:           localDateTimeToApi(timeSlotEnd),
      confirmado:    modal.data?.confirmado ?? false,
      confirmToken:  modal.data?.confirmToken || Math.random().toString(36).substring(2, 12),
      observaciones: form.observaciones?.value || "",
      monto:         montoTurno ? parseFloat(montoTurno)
        : (nuevaPrestacion.precio ? parseFloat(nuevaPrestacion.precio) : null),
    };

    try {
      const url    = modal.data ? `${API}/turnos/${modal.data.id}` : `${API}/turnos`;
      const method = modal.data ? "PUT" : "POST";
      const res    = await fetch(url, { method, headers: jsonAuthHeaders(), body: JSON.stringify(payload) });
      const data   = await res.json();

      if (!res.ok) {
        setSaveResult({ saved: false, errorMsg: data.message || "Error al guardar el turno" });
        return;
      }

      await fetchTurnos();
      setSaveResult({ saved: true, waStatus: data.wa_status ?? null });
    } catch (err) {
      setSaveResult({ saved: false, errorMsg: "Error de conexión" });
    }
  };

  // ── Reenviar WhatsApp (botón en edición) ──

  const handleReenviarWA = async () => {
    const turnoId = modal.data?.id ?? saveResult?.turnoId;
    if (!turnoId) return;
    setWaInlineStatus("enviando");
    try {
      const res  = await fetch(`${API}/turnos/${turnoId}/reenviar-wa`, {
        method: "POST",
        headers: jsonAuthHeaders(),
      });
      const data = await res.json();
      if (data.success && data.wa_status?.sent) {
        setWaInlineStatus({ ok: true });
      } else if (data.success && data.wa_status?.queued) {
        setWaInlineStatus({
          ok: true,
          queued: true,
          reason: data.wa_status.message || "WhatsApp en envío",
        });
      } else {
        setWaInlineStatus({ error: true, reason: data.wa_status?.reason || "Error desconocido" });
      }
      // Si el reintento viene del banner post-guardado, actualizar saveResult también
      if (saveResult) {
        setSaveResult(prev => ({ ...prev, waStatus: data.wa_status }));
      }
    } catch {
      setWaInlineStatus({ error: true, reason: "Error de conexión" });
    }
  };

  // ── Eliminar ──

  const handleDelete = async () => {
    if (!modal.data) return;
    if (!window.confirm("¿Eliminás este turno de forma definitiva? No quedará registro en el calendario.")) return;
    try {
      await fetch(`${API}/turnos/${modal.data.id}`, { method: "DELETE", headers: jsonAuthHeaders() });
      await fetchTurnos();
      closeModal();
    } catch (e) { console.error("Error eliminando turno:", e); }
  };

  // ── Cancelar (mantiene registro) ──

  const handleCancelar = async () => {
    if (!modal.data) return;
    if (!window.confirm("¿Marcar este turno como cancelado? El horario queda libre para otro turno y queda el registro en el calendario.")) return;
    setCancelActionStatus("procesando");
    try {
      const res = await fetch(`${API}/turnos/${modal.data.id}/cancelar`, {
        method: "POST",
        headers: jsonAuthHeaders(),
        body: JSON.stringify({ motivo: "usuario" }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setCancelActionStatus({ error: true, reason: data.message || "No se pudo cancelar el turno" });
        return;
      }
      await fetchTurnos();
      setModal(prev => ({ ...prev, data: { ...prev.data, ...data.data, start: parseApiDateTime(data.data.start), end: parseApiDateTime(data.data.end) } }));
      setCancelActionStatus({ ok: true, accion: "cancelado" });
    } catch {
      setCancelActionStatus({ error: true, reason: "Error de conexión" });
    }
  };

  const handleReactivar = async () => {
    if (!modal.data) return;
    setCancelActionStatus("procesando");
    try {
      const res = await fetch(`${API}/turnos/${modal.data.id}/reactivar`, {
        method: "POST",
        headers: jsonAuthHeaders(),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setCancelActionStatus({ error: true, reason: data.message || "No se pudo reactivar el turno" });
        return;
      }
      await fetchTurnos();
      setModal(prev => ({ ...prev, data: { ...prev.data, ...data.data, start: parseApiDateTime(data.data.start), end: parseApiDateTime(data.data.end) } }));
      setCancelActionStatus({ ok: true, accion: "reactivado" });
    } catch {
      setCancelActionStatus({ error: true, reason: "Error de conexión" });
    }
  };

  const formatFechaHora = value => {
    if (!value) return "";
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const pad = n => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const motivoLabel = motivo => {
    switch (motivo) {
      case "paciente_wa": return "Cancelado por el paciente vía WhatsApp";
      case "usuario":     return "Cancelado manualmente desde el sistema";
      default:            return motivo ? `Motivo: ${motivo}` : "Sin motivo registrado";
    }
  };

  // ── Render ──

  const dayLabelLine = vista === "day" ? formatDayLine(fechaCalendario) : null;
  const dayIsToday = vista === "day" && isToday(fechaCalendario);

  return (
    <div ref={calendarioContainerRef} className="calendario-container">
      {dayLabelLine && (
        <CalendarioDayAllDayLabel
          label={dayLabelLine}
          isToday={dayIsToday}
          rootRef={calendarioContainerRef}
          fechaKey={fechaCalendario.toISOString()}
        />
      )}

      {loading && <p className="calendario-loading">Cargando turnos...</p>}

      <CalendarioDesktopBar
        profSel={profSel}
        onProfChange={setProfSel}
        profesionales={profesionales}
      />

      {configOpen && (
        <>
          <button
            type="button"
            className="cal-config-overlay"
            aria-label="Cerrar configuración"
            onClick={() => setConfigOpen(false)}
          />
          <CalendarioConfigPanel
            vista={vista}
            onViewChange={setVista}
            profSel={profSel}
            onProfChange={setProfSel}
            profesionales={profesionales}
            horaDesde={horaDesde}
            horaHasta={horaHasta}
            onHoraDesdeChange={handleHoraDesdeChange}
            onHoraHastaChange={handleHoraHastaChange}
            onClose={() => setConfigOpen(false)}
          />
        </>
      )}

      <Calendar
        className={`calendario calendario-view--${vista}`}
        style={{ flex: 1, minHeight: 0 }}
        localizer={localizer}
        culture="es"
        events={[...eventosFiltrados, ...bloquesEventos]}
        startAccessor="start"
        endAccessor="end"
        popup
        selectable
        longPressThreshold={0}
        views={{ month: true, week: true, day: true, agenda: true }}
        view={vista}
        onView={setVista}
        date={fechaCalendario}
        onNavigate={handleNavigate}
        onDrillDown={handleDrillDown}
        defaultView="week"
        min={minTime}
        max={maxTime}
        onSelectSlot={handleSelectSlot}
        onSelectEvent={handleSelectEvent}
        step={step}
        timeslots={timeslots}
        components={calendarComponents}
        messages={{
          next: "›", previous: "‹", today: "Hoy",
          month: "Mes", week: "Semana", day: "Día", agenda: "Agenda",
          allDay: "",
          showMore: total => `+${total} más`,
        }}
        eventPropGetter={event => {
          if (event.type === "block") {
            return {
              className: "evento-bloqueo",
              style: {
                backgroundColor: "rgba(254,226,226,0.85)",
                color: "#991b1b",
                borderRadius: 4,
                border: "1px solid #fecaca",
                fontWeight: 500,
                fontSize: "0.75rem",
                cursor: "default",
              },
            };
          }
          const eventBg = event.color || "#032548";
          if (event.cancelado) {
            return { className: "evento-cancelado" };
          }
          if (event.confirmado) {
            return {
              className: "evento-confirmado",
              style: { "--event-bg": eventBg },
            };
          }
          return {
            className: "evento-pendiente",
            style: { "--event-bg": eventBg },
          };
        }}
        dayPropGetter={dayPropGetter}
        slotPropGetter={slotPropGetter}
      />

      {/* ── Modal de turno ── */}
      {modal.open && (
        <div className="modal-overlay">
          <form className="modal-form" onSubmit={handleSave}>

            {/* Header sticky */}
            <div className="modal-header-inline">
              <div className="modal-header-left">
                <h3>
                  {modal.data?.cancelado
                    ? "Turno cancelado"
                    : modal.data ? "Editar turno" : "Nuevo turno"}
                </h3>
                {modal.data && (
                  <span className={`confirm-badge ${
                    modal.data.cancelado ? "badge-cancelled"
                    : modal.data.confirmado ? "badge-ok" : "badge-pending"
                  }`}>
                    {modal.data.cancelado
                      ? "❌ Cancelado"
                      : modal.data.confirmado ? "✅ Confirmado" : "⏳ Pendiente de confirmación"}
                  </span>
                )}
              </div>
              <button type="button" className="modal-close-btn" onClick={closeModal} aria-label="Cerrar">×</button>
            </div>

            {/* Vista de turno cancelado (solo lectura) */}
            {modal.data?.cancelado && !saveResult ? (
              <div className="cancelado-detail">
                <div className="cancelado-banner">
                  <div className="cancelado-banner__icon">❌</div>
                  <div className="cancelado-banner__body">
                    <div className="cancelado-banner__title">Este turno fue cancelado</div>
                    <div className="cancelado-banner__motivo">{motivoLabel(modal.data.cancelado_motivo)}</div>
                    {modal.data.cancelado_en && (
                      <div className="cancelado-banner__fecha">
                        Cancelado el {formatFechaHora(modal.data.cancelado_en)}
                      </div>
                    )}
                  </div>
                </div>

                <div className="cancelado-info-grid">
                  <div className="cancelado-info-row">
                    <span className="cancelado-info-label">Paciente</span>
                    <span className="cancelado-info-value">{modal.data.paciente || "—"}</span>
                  </div>
                  <div className="cancelado-info-row">
                    <span className="cancelado-info-label">Profesional</span>
                    <span className="cancelado-info-value">{modal.data.profesional || "—"}</span>
                  </div>
                  <div className="cancelado-info-row">
                    <span className="cancelado-info-label">Servicio</span>
                    <span className="cancelado-info-value">{modal.data.title || "—"}</span>
                  </div>
                  <div className="cancelado-info-row">
                    <span className="cancelado-info-label">Fecha y hora</span>
                    <span className="cancelado-info-value">
                      {formatFechaHora(modal.data.start)} – {formatFechaHora(modal.data.end).split(" ")[1] || ""}
                    </span>
                  </div>
                  {modal.data.observaciones && (
                    <div className="cancelado-info-row cancelado-info-row--full">
                      <span className="cancelado-info-label">Observaciones</span>
                      <span className="cancelado-info-value">{modal.data.observaciones}</span>
                    </div>
                  )}
                </div>

                {cancelActionStatus && cancelActionStatus !== "procesando" && (
                  <div className={`wa-inline-status ${cancelActionStatus.ok ? "wa-ok" : "wa-err"}`}>
                    {cancelActionStatus.ok
                      ? `✅ Turno ${cancelActionStatus.accion}`
                      : `⚠️ ${cancelActionStatus.reason}`}
                  </div>
                )}
                {cancelActionStatus === "procesando" && (
                  <div className="wa-inline-status wa-sending">⏳ Procesando…</div>
                )}

                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleReactivar}
                    disabled={cancelActionStatus === "procesando"}
                  >
                    Reactivar turno
                  </button>
                  <button
                    type="button"
                    className="btn-danger"
                    onClick={handleDelete}
                    disabled={cancelActionStatus === "procesando"}
                  >
                    Eliminar definitivamente
                  </button>
                  {modal.data?.telefono && (
                    <a
                      href={`https://wa.me/${normalizePhoneForWaMe(modal.data.telefono)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-wa-direct"
                    >
                      💬 Contactar directo
                    </a>
                  )}
                  <button type="button" className="btn-secondary" onClick={closeModal}>
                    Cerrar
                  </button>
                </div>
              </div>
            ) : saveResult ? (
              <div className={`save-result-banner ${saveResult.saved ? "banner-success" : "banner-error"}`}>
                <div className="save-result-icon">{saveResult.saved ? "✅" : "❌"}</div>
                <div className="save-result-body">
                  <div className="save-result-title">
                    {saveResult.saved ? "Turno guardado correctamente" : `Error: ${saveResult.errorMsg}`}
                  </div>
                  {saveResult.saved && saveResult.waStatus && (() => {
                    const waLine = getWaStatusDisplay(saveResult.waStatus);
                    if (!waLine) return null;
                    return (
                      <div className={`save-wa-line ${waLine.tone}`}>
                        {waLine.text}
                      </div>
                    );
                  })()}
                  {/* Resultado del reintento */}
                  {waInlineStatus && waInlineStatus !== "enviando" && (
                    <div className={`save-wa-line ${waInlineStatus.ok ? (waInlineStatus.queued ? "wa-pending" : "wa-sent") : "wa-not-sent"}`}>
                      {waInlineStatus.ok
                        ? (waInlineStatus.queued
                          ? `📲 ${waInlineStatus.reason || "WhatsApp en envío"}`
                          : "✅ WhatsApp enviado correctamente")
                        : `⚠️ ${waInlineStatus.reason}`}
                    </div>
                  )}
                  {waInlineStatus === "enviando" && (
                    <div className="save-wa-line">⏳ Enviando...</div>
                  )}
                </div>
                <div className="save-result-actions">
                  <button type="button" className="btn-primary" onClick={closeModal}>Cerrar</button>
                  {saveResult.saved && saveResult.waStatus && !saveResult.waStatus.sent && !saveResult.waStatus.queued && !waInlineStatus?.ok && (
                    <button
                      type="button"
                      className="btn-wa-retry"
                      onClick={handleReenviarWA}
                      disabled={waInlineStatus === "enviando"}
                    >
                      {waInlineStatus === "enviando" ? "Enviando…" : "Reintentar envío WA"}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <>
                {formErrors.length > 0 && (
                  <div className="form-errors-banner" role="alert">
                    <ul>
                      {formErrors.map((msg, i) => (
                        <li key={i}>{msg}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* ── Card 1: Profesional ── */}
                <div className="modal-card">
                  <div className="modal-card__header">
                    <span className="modal-card__title">Profesional</span>
                  </div>
                  {user?.role === "profesional" ? (
                    <div className="modal-field-readonly">
                      {user?.profesional?.apellido}, {user?.profesional?.nombre}
                    </div>
                  ) : (
                    <select
                      id="turno-profesional-id"
                      className="input"
                      name="profesional_id"
                      value={modalProfesionalId}
                      onChange={handleProfesionalChange}
                      required
                    >
                      <option value="">— Seleccionar profesional —</option>
                      {profesionales.map(p => (
                        <option key={p.id} value={p.id}>{p.apellido}, {p.nombre}</option>
                      ))}
                    </select>
                  )}
                </div>

                {/* ── Card 2: Paciente ── */}
                <div className="modal-card">
                  <div className="modal-card__header">
                    <span className="modal-card__title">Paciente</span>
                    {!isNuevoPaciente && (
                      <button
                        type="button"
                        className="btn-card-atajo"
                        onClick={() => setIsNuevoPaciente(true)}
                      >
                        + Nuevo paciente
                      </button>
                    )}
                  </div>
                  {isNuevoPaciente ? (
                    <>
                      <div className="card-atajo-bar">
                        <span className="card-atajo-label">Datos del nuevo paciente</span>
                        <button
                          type="button"
                          className="btn-atajo-cancel"
                          onClick={() => { setIsNuevoPaciente(false); setNuevoPaciente(EMPTY_NUEVO_PACIENTE); }}
                        >
                          Cancelar
                        </button>
                      </div>
                      <div className="input-row">
                        <input
                          id="turno-paciente-nombre"
                          name="paciente_nuevo_nombre"
                          className="input"
                          placeholder="Nombre *"
                          value={nuevoPaciente.nombre}
                          onChange={e => setNuevoPaciente(p => ({ ...p, nombre: e.target.value }))}
                        />
                        <input
                          id="turno-paciente-apellido"
                          name="paciente_nuevo_apellido"
                          className="input"
                          placeholder="Apellido *"
                          value={nuevoPaciente.apellido}
                          onChange={e => setNuevoPaciente(p => ({ ...p, apellido: e.target.value }))}
                        />
                      </div>
                      <div className="input-row">
                        <input
                          id="turno-paciente-dni"
                          name="paciente_nuevo_dni"
                          className="input"
                          placeholder="DNI *"
                          value={nuevoPaciente.dni}
                          required
                          onChange={e => setNuevoPaciente(p => ({ ...p, dni: e.target.value }))}
                        />
                        <input
                          id="turno-paciente-telefono"
                          name="paciente_nuevo_telefono"
                          className="input"
                          type="tel"
                          placeholder="Teléfono"
                          value={nuevoPaciente.telefono}
                          onChange={e => setNuevoPaciente(p => ({ ...p, telefono: e.target.value }))}
                        />
                      </div>
                      <input
                        id="turno-paciente-mail"
                        name="paciente_nuevo_mail"
                        className="input"
                        type="email"
                        placeholder="Email"
                        value={nuevoPaciente.mail}
                        onChange={e => setNuevoPaciente(p => ({ ...p, mail: e.target.value }))}
                      />
                    </>
                  ) : (
                    <select
                      id="turno-paciente-id"
                      className="input"
                      name="paciente_id"
                      defaultValue={modal.data?.paciente_id || ""}
                    >
                      <option value="">— Seleccionar paciente —</option>
                      {pacientes.map(p => (
                        <option key={p.id} value={p.id}>{p.apellido}, {p.nombre}</option>
                      ))}
                    </select>
                  )}
                </div>

                {/* ── Card 3: Servicio ── */}
                <div className="modal-card">
                  <div className="modal-card__header">
                    <span className="modal-card__title">Servicio / Prestación</span>
                    {!isNuevaPrestacion && (
                      <button
                        type="button"
                        className="btn-card-atajo"
                        onClick={() => {
                          setIsNuevaPrestacion(true);
                          setServicioSel("");
                          setTituloTurno("");
                          setMontoTurno("");
                        }}
                      >
                        + Nueva prestación
                      </button>
                    )}
                  </div>

                  {isNuevaPrestacion ? (
                    <>
                      <div className="card-atajo-bar">
                        <span className="card-atajo-label">Nueva prestación</span>
                        <button
                          type="button"
                          className="btn-atajo-cancel"
                          onClick={() => { setIsNuevaPrestacion(false); setNuevaPrestacion(EMPTY_NUEVA_PRESTACION); }}
                        >
                          Cancelar
                        </button>
                      </div>
                      <input
                        id="turno-prestacion-nombre"
                        name="prestacion_nueva_nombre"
                        className="input"
                        placeholder="Nombre de la prestación *"
                        value={nuevaPrestacion.nombre}
                        onChange={e => setNuevaPrestacion(p => ({ ...p, nombre: e.target.value }))}
                      />
                      <div className="input-row">
                        <input
                          id="turno-prestacion-precio"
                          name="prestacion_nueva_precio"
                          className="input"
                          type="number"
                          placeholder="Precio ($)"
                          value={nuevaPrestacion.precio}
                          onChange={e => {
                            setNuevaPrestacion(p => ({ ...p, precio: e.target.value }));
                            setMontoTurno(e.target.value);
                          }}
                        />
                        <input
                          id="turno-prestacion-duracion"
                          name="prestacion_nueva_duracion"
                          className="input"
                          type="number"
                          placeholder="Duración (min)"
                          value={nuevaPrestacion.duracion_minutos}
                          onChange={e => setNuevaPrestacion(p => ({ ...p, duracion_minutos: e.target.value }))}
                        />
                      </div>
                      <input
                        id="turno-prestacion-descripcion"
                        name="prestacion_nueva_descripcion"
                        className="input"
                        placeholder="Descripción (opcional)"
                        value={nuevaPrestacion.descripcion}
                        onChange={e => setNuevaPrestacion(p => ({ ...p, descripcion: e.target.value }))}
                      />
                    </>
                  ) : (
                    <>
                      <select
                        id="turno-servicio-id"
                        name="servicio_id"
                        className="input"
                        value={servicioSel}
                        onChange={handleServicioChange}
                        disabled={!modalProfesionalId && user?.role !== "profesional"}
                      >
                        <option value="">
                          {(!modalProfesionalId && user?.role !== "profesional")
                            ? "— Primero seleccioná un profesional —"
                            : "— Seleccionar servicio —"}
                        </option>
                        {practicasProf.map(s => (
                          <option key={s.id} value={s.id}>
                            {s.nombre}
                            {s.duracion_minutos ? ` · ${s.duracion_minutos} min` : ""}
                            {s.costo_base != null ? ` · $${s.costo_base}` : ""}
                          </option>
                        ))}
                      </select>

                      {servicioSelObj && (
                        <div className="servicio-info-row">
                          {servicioSelObj.costo_base != null && (
                            <div className="servicio-info-tag">
                              <span className="tag-label">Precio base</span>
                              <span className="tag-value">${servicioSelObj.costo_base}</span>
                            </div>
                          )}
                          {servicioSelObj.duracion_minutos && (
                            <div className="servicio-info-tag">
                              <span className="tag-label">Duración</span>
                              <span className="tag-value">{servicioSelObj.duracion_minutos} min</span>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* Precio del turno (siempre visible) */}
                  <div className="precio-row">
                    <label className="precio-label" htmlFor="turno-monto">Precio del turno</label>
                    <div className="precio-input-wrap">
                      <span className="precio-symbol">$</span>
                      <input
                        id="turno-monto"
                        name="monto"
                        className="input precio-input"
                        type="number"
                        placeholder="0"
                        min="0"
                        step="0.01"
                        value={montoTurno}
                        onChange={e => setMontoTurno(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* ── Card 4: Horario ── */}
                <div className="modal-card">
                  <div className="modal-card__header">
                    <span className="modal-card__title">Día y horario</span>
                    {currentServiceDuration && (
                      <span className="modal-card__hint">{currentServiceDuration} min por defecto</span>
                    )}
                  </div>
                  <TimeSlotPicker
                    startTime={timeSlotStart}
                    endTime={timeSlotEnd}
                    onStartChange={setTimeSlotStart}
                    onEndChange={setTimeSlotEnd}
                    durationMinutes={currentServiceDuration}
                  />
                </div>

                {/* ── Card 5: Observaciones ── */}
                <div className="modal-card">
                  <div className="modal-card__header">
                    <span className="modal-card__title">Observaciones</span>
                  </div>
                  <textarea
                    id="turno-observaciones"
                    className="input"
                    name="observaciones"
                    placeholder="Notas o indicaciones adicionales (opcional)"
                    defaultValue={modal.data?.observaciones || ""}
                    rows={3}
                  />
                </div>

                {/* Feedback de reenvío manual */}
                {waInlineStatus && (
                  <div className={`wa-inline-status ${waInlineStatus === "enviando" ? "wa-sending"
                    : waInlineStatus.ok ? (waInlineStatus.queued ? "wa-pending" : "wa-ok") : "wa-err"}`}>
                    {waInlineStatus === "enviando" && "⏳ Enviando WhatsApp..."}
                    {waInlineStatus.ok && !waInlineStatus.queued && "✅ WhatsApp enviado correctamente"}
                    {waInlineStatus.ok && waInlineStatus.queued && `📲 ${waInlineStatus.reason || "WhatsApp en envío"}`}
                    {waInlineStatus.error && `⚠️ ${waInlineStatus.reason}`}
                  </div>
                )}

                {/* Feedback de cancelación manual */}
                {cancelActionStatus && cancelActionStatus !== "procesando" && !cancelActionStatus.ok && (
                  <div className="wa-inline-status wa-err">⚠️ {cancelActionStatus.reason}</div>
                )}
                {cancelActionStatus === "procesando" && (
                  <div className="wa-inline-status wa-sending">⏳ Procesando…</div>
                )}

                {/* Acciones */}
                <div className="modal-actions">
                  <button type="submit" className="btn-primary">Guardar</button>
                  {modal.data && !modal.data.confirmado && !modal.data.cancelado && (
                    <button
                      type="button"
                      className="btn-wa"
                      onClick={handleReenviarWA}
                      disabled={waInlineStatus === "enviando"}
                    >
                      📲 Reenviar WhatsApp
                    </button>
                  )}
                  {modal.data?.telefono && (
                    <a
                      href={`https://wa.me/${normalizePhoneForWaMe(modal.data.telefono)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-wa-direct"
                    >
                      💬 Contactar directo
                    </a>
                  )}
                  {modal.data && (
                    <button
                      type="button"
                      onClick={handleCancelar}
                      className="btn-cancel-soft"
                      disabled={cancelActionStatus === "procesando"}
                    >
                      Cancelar turno
                    </button>
                  )}
                  {modal.data && (
                    <button type="button" onClick={handleDelete} className="btn-danger">
                      Eliminar
                    </button>
                  )}
                  <button type="button" onClick={closeModal} className="btn-secondary">
                    Cerrar
                  </button>
                </div>
              </>
            )}
          </form>
        </div>
      )}

      {isPrestacionesModalOpen && selectedPacienteForPrestacion && (
        <PrestacionesModal
          pacienteId={selectedPacienteForPrestacion.id}
          pacienteNombre={selectedPacienteForPrestacion.nombre}
          isOpen={isPrestacionesModalOpen}
          onClose={() => { setIsPrestacionesModalOpen(false); setSelectedPacienteForPrestacion(null); }}
          onPrestacionAgregada={() => {}}
        />
      )}
    </div>
  );
}
